"use client";

import type React from "react";
import Image from "next/image";
import bgImage from "./images/listing-latest-bg.png";
import type { CategoryLatestVideo, CategoryLatestNews } from "@/types/dto/category.dto";

interface CategoryLatestSectionProps {
	videos?: CategoryLatestVideo[];
	news?: CategoryLatestNews[];
	title?: string;
}

const Card = ({
	children,
	className = "",
	onClick,
	...props
}: {
	children: React.ReactNode;
	className?: string;
	onClick?: () => void;
} & React.HTMLAttributes<HTMLDivElement>) => {
	return (
		<div
			className={`overflow-hidden rounded-2xl bg-white shadow-lg transition-shadow duration-300 hover:shadow-xl ${className}`}
			onClick={onClick}
			{...props}
		>
			{children}
		</div>
	);
};

const LiveBadge = () => {
	return (
		<div className="absolute right-3 top-3 rounded-md bg-red-600 px-3 py-1 text-xs font-semibold text-white">
			LIVE
		</div>
	);
};

// Function to extract YouTube video ID from URL
const extractYouTubeVideoId = (url: string): string | null => {
	const regex =
		/(?:youtube\.com\/(?:[^/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?/\s]{11})/;
	const match = url.match(regex);
	return match ? match[1] : null;
};

// Function to create YouTube embed URL
const createYouTubeEmbedUrl = (videoId: string): string => {
	return `https://www.youtube.com/embed/${videoId}?autoplay=0&mute=1&controls=1&showinfo=0&rel=0`;
};

export default function CategoryLatestSection({
	videos = [],
	news = [],
	title = "",
}: CategoryLatestSectionProps) {
	if (!videos?.length && !news?.length) {
		console.log("CategoryLatestSection - No data to display");
		return null;
	}

	return (
		<div
			className="mt-20 w-full rounded-lg px-6 py-12"
			style={{
				backgroundImage: `url(${bgImage.src})`,
				backgroundSize: "cover",
				backgroundPosition: "center",
				backgroundRepeat: "no-repeat",
			}}
		>
			<div className="mx-auto max-w-6xl">
				{/* Latest in CRM Videos Section */}
				{videos?.length > 0 && (
					<div className="mb-20">
						<h2 className="mb-8 text-center text-3xl font-semibold text-white">
							{`Latest in ${title} Videos`}
						</h2>
						<div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
							{videos.slice(0, 3).map((video, index) => {
								const videoId = extractYouTubeVideoId(video.youtube_link);
								const embedUrl = videoId ? createYouTubeEmbedUrl(videoId) : null;
								return (
									<Card
										key={`video-${index}`}
										className={`cursor-pointer transition-all duration-300 ${
											index === 1 ? "lg:z-10 lg:scale-105" : ""
										}`}
									>
										<div className="relative">
											{embedUrl ? (
												<div className="relative h-48 w-full">
													<iframe
														src={embedUrl}
														title={video.video_title}
														allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
														allowFullScreen
														className="aspect-video h-full w-full rounded-t-2xl"
														suppressHydrationWarning
													/>
													<LiveBadge />
												</div>
											) : (
												<div className="relative">
													<Image
														src="/placeholder.svg?height=192&width=400"
														alt={video.video_title}
														width={400}
														height={192}
														className="h-48 w-full object-cover"
													/>
													<LiveBadge />
												</div>
											)}
										</div>
										<div className="p-4">
											<h3 className="text-sm font-medium leading-snug text-gray-900">
												{video.video_title}
											</h3>
										</div>
									</Card>
								);
							})}
						</div>
					</div>
				)}

				{/* Latest in CRM News Section */}
				{news?.length > 0 && (
					<div className="mt-16">
						<h2 className="mb-8 text-center text-3xl font-semibold text-white">
							{`Latest in ${title} News`}
						</h2>
						<div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
							{news.slice(0, 3).map((item, index) => {
								return (
									<Card
										key={`news-${index}`}
										className={`cursor-pointer transition-all duration-300 ${
											index === 1 ? "lg:z-10 lg:scale-105" : ""
										}`}
										onClick={() => {
											if (item.news_link) {
												window.open(item.news_link, "_blank");
											}
										}}
									>
										<div className="relative">
											<img
												src={
													item.img_url ||
													"/placeholder.svg?height=192&width=400&query=news article thumbnail" ||
													"/placeholder.svg" ||
													"/placeholder.svg"
												}
												alt={item.title}
												width={400}
												height={192}
												className="h-48 w-full object-cover"
											/>
										</div>
										<div className="p-4">
											<h3 className="mb-2 text-sm font-medium leading-snug text-gray-900">
												{item.title}
											</h3>
											<p className="text-xs text-gray-600">Click to read more</p>
										</div>
									</Card>
								);
							})}
						</div>
					</div>
				)}
			</div>
		</div>
	);
}
